/**
 * video-to-sound/prepare.js — window.VideoSoundPrepare
 *
 * ONE decode pass over the chosen range gives everything the tool needs before
 * a single token is spent:
 *
 *   proxy   a small SILENT 480p H.264 copy of the range — the only video that
 *           is sent to the server, sized to pass its upload cap whatever the
 *           source (proxyCeiling); the original stays on the page for the
 *           final mux
 *   cuts    scene boundaries (colour-histogram jumps, ≥ 1 s apart) — they
 *           become the scenes a person describes and re-voices one by one
 *   frames  one JPEG per second (384 px) — the scene analysis picks from them
 *   thumbs  a filmstrip for the timeline
 *
 * Lanes: WebCodecsPipeline (hardware decode + encode, mp4/mov) first; FFmpeg-
 * WASM for everything else (webm, mkv, avi…, or a WebCodecs refusal), where the
 * analysis then runs over the proxy with a hidden <video> — the proxy is
 * H.264 and plays everywhere.
 *
 *   VideoSoundPrepare.run({ file, start, end, meta:{width,height}, slug,
 *                           onProgress(ratio, phase), signal })
 *     → { proxy:Blob, lane, duration, width, height,
 *         cuts:[sec…], frames:[{t, blob}], thumbs:[{t, url}] }   (t relative to start)
 */
(function (w) {
    'use strict';
    if (w.VideoSoundPrepare) return;

    var SHORT_SIDE = 480, LONG_CAP = 854;
    /* The proxy must fit the upload door — a BYTE budget, not a bitrate.
       Without a ceiling the pipeline keeps 85 % of the source's bits (and the
       software rung ×2.2 on top): a 10 Mbit/s 720p clip gave a 3.9 Mbit/s
       «480p» copy, 87.6 MB for 3 minutes — eleven 413s in a row, 2026-10-03;
       phone proxies reached 7.4 Mbit/s. A fixed low ceiling is the wrong shape:
       measured 2026-10-15 in Chrome on real 720p footage (VMAF against the same
       path at 20 Mbit/s; «mustard» 9.9 Mbit/s fine detail / «dogwalk» 6.8),
       hardware · software rung:
         1.2 Mbit/s  77.5 · 58.3 / 92.1 · 80.4      2.5  95.3 · 78.9 / 98.8 · 93.9
         2.0         91.4 · 71.5 / 97.9 · 90.6      3.0  97.6 · 85.1 / 99.1 · 95.9
         none (≈4)   99.1 · 93.9 / 99.1 · 98.4
       So the proxy keeps the bits the engines were chosen on (bake-off
       2026-09-24) wherever they fit, and only a long heavy one is held to what
       fits: PROXY_FILL of the cap over its seconds — 80 MB and 3 min → 2.98
       Mbit/s; under ~2 min nothing changes. FILL 0.8: the hardware encoder
       overshot its target by 8–13 % at 2.5–3 Mbit/s. */
    var PROXY_FILL = 0.8;
    function proxyCeiling(maxBytes, sec) {
        return (maxBytes > 0 && sec > 0) ? Math.floor(maxBytes * 8 * PROXY_FILL / sec) : 0;
    }
    var FRAME_EVERY = 1.0, FRAME_W = 384;
    var THUMBS = 40, THUMB_H = 72;
    var MIN_SCENE = 1.0;

    function even(n) { return Math.max(2, Math.round(n / 2) * 2); }
    function outSize(dw, dh) {
        if (!(dw > 0 && dh > 0)) return { w: 854, h: 480 };
        var s = SHORT_SIDE / Math.min(dw, dh);
        var W = dw * s, H = dh * s;
        var lc = LONG_CAP / Math.max(W, H);
        if (lc < 1) { W *= lc; H *= lc; }
        return { w: even(W), h: even(H) };
    }
    function ae(slug, action, extra) {
        try { w.__ae && w.__ae('feature_use', Object.assign({ slug: slug, action: action }, extra || {})); } catch (_) {}
    }

    /* Colour histogram of a 32×18 thumbnail, 8 bins per channel. The L1 distance
       between consecutive frames jumps at a hard cut and stays low through
       motion and pans — the classic shot-boundary signal, cheap enough to run on
       every frame of the pass. */
    function Hist() {
        var c = new OffscreenCanvas(32, 18);
        var x = c.getContext('2d', { willReadFrequently: true });
        return {
            of: function (src, sw, sh) {
                x.drawImage(src, 0, 0, sw, sh, 0, 0, 32, 18);
                var d = x.getImageData(0, 0, 32, 18).data, h = new Float32Array(24);
                for (var i = 0; i < d.length; i += 4) { h[d[i] >> 5]++; h[8 + (d[i + 1] >> 5)]++; h[16 + (d[i + 2] >> 5)]++; }
                for (var j = 0; j < 24; j++) h[j] /= 576;
                return h;
            }
        };
    }
    function dist(a, b) { var s = 0; for (var i = 0; i < 24; i++) s += Math.abs(a[i] - b[i]); return s / 3; }

    /* Cuts from per-frame scores: a jump well above the local median and above
       an absolute floor, never closer than MIN_SCENE to the previous cut. */
    function cutsFrom(scores, total) {
        var cuts = [], last = 0;
        for (var i = 1; i < scores.length; i++) {
            var s = scores[i].s;
            if (s < 0.28) continue;
            var lo = Math.max(0, i - 12), hi = Math.min(scores.length, i + 13), win = [];
            for (var k = lo; k < hi; k++) if (k !== i) win.push(scores[k].s);
            win.sort(function (a, b) { return a - b; });
            var med = win.length ? win[win.length >> 1] : 0;
            if (s < Math.max(0.28, med * 3.5)) continue;
            var t = scores[i].t;
            if (t - last < MIN_SCENE || total - t < MIN_SCENE) continue;
            cuts.push(Math.round(t * 1000) / 1000);
            last = t;
        }
        return cuts;
    }

    function Collector(total) {
        var frames = [], thumbs = [], pending = [], nextFrame = 0, nextThumb = 0;
        var thumbStep = Math.max(0.25, total / THUMBS);
        return {
            maybe: function (src, sw, sh, t) {
                if (t + 1e-3 >= nextFrame) {
                    nextFrame = t + FRAME_EVERY;
                    var fh = Math.round(FRAME_W * sh / sw), fc = new OffscreenCanvas(FRAME_W, even(fh));
                    fc.getContext('2d').drawImage(src, 0, 0, sw, sh, 0, 0, fc.width, fc.height);
                    pending.push(fc.convertToBlob({ type: 'image/jpeg', quality: 0.72 }).then(function (b) { frames.push({ t: t, blob: b }); }));
                }
                if (t + 1e-3 >= nextThumb) {
                    nextThumb = t + thumbStep;
                    var tw = even(THUMB_H * sw / sh), tc = new OffscreenCanvas(tw, THUMB_H);
                    tc.getContext('2d').drawImage(src, 0, 0, sw, sh, 0, 0, tw, THUMB_H);
                    pending.push(tc.convertToBlob({ type: 'image/jpeg', quality: 0.7 }).then(function (b) { thumbs.push({ t: t, url: URL.createObjectURL(b) }); }));
                }
            },
            done: function () {
                return Promise.all(pending).then(function () {
                    frames.sort(function (a, b) { return a.t - b.t; });
                    thumbs.sort(function (a, b) { return a.t - b.t; });
                    return { frames: frames, thumbs: thumbs };
                });
            }
        };
    }

    async function viaWebCodecs(o) {
        if (!w.WebCodecsPipeline || typeof OffscreenCanvas === 'undefined') throw new Error('no-webcodecs');
        if (!/\.(mp4|m4v|mov|3gp)$/i.test(o.file.name || '') && !/mp4|quicktime|3gpp/i.test(o.file.type || '')) throw new Error('not-mp4');
        var total = o.end - o.start;
        var sz = outSize(o.meta.width, o.meta.height);
        var hist = Hist(), scores = [], prev = null, t0 = null;
        var col = Collector(total);
        var res = await w.WebCodecsPipeline.run({
            slug: o.slug, file: o.file, params: {}, crf: 28, maxVideoBitrate: o.ceilingBps || 0,
            trimStart: o.start > 0.01 ? o.start : 0,
            trimEnd: o.end < o.meta.duration - 0.05 ? o.end : 0,
            dropAudio: true,
            geometry2d: {
                width: sz.w, height: sz.h,
                draw: function (ctx, video, W, H, idx, tSec, _f, info) {
                    var dw = (info && info.dispW) || o.meta.width || W, dh = (info && info.dispH) || o.meta.height || H;
                    video(0, 0, dw, dh, 0, 0, W, H);
                    if (t0 === null) t0 = tSec;
                    var t = Math.max(0, tSec - t0);
                    var h = hist.of(ctx.canvas, W, H);
                    scores.push({ t: t, s: prev ? dist(h, prev) : 0 });
                    prev = h;
                    col.maybe(ctx.canvas, W, H, t);
                }
            },
            onProgress: function (p) { o.onProgress && o.onProgress(Math.min(0.99, p), 'webcodecs'); },
            abortSignal: o.signal
        });
        if (!res || !res.blob || res.blob.size < 1000) throw new Error('webcodecs-empty');
        var got = await col.done();
        return { proxy: res.blob, lane: 'webcodecs', duration: total, width: sz.w, height: sz.h,
                 cuts: cutsFrom(scores, total), frames: got.frames, thumbs: got.thumbs };
    }

    async function viaFfmpeg(o) {
        var F = w.FFmpegProcessor;
        if (!F) throw new Error('no-ffmpeg');
        if (!F.isReady || !F.isReady()) {
            if (typeof w.__ensureFFmpeg === 'function') await w.__ensureFFmpeg();
            if (!F.isReady || !F.isReady()) await F.init();
        }
        var total = o.end - o.start;
        var sz = outSize(o.meta.width, o.meta.height);
        var vf = 'scale=' + sz.w + ':' + sz.h + ':force_original_aspect_ratio=decrease,pad=' + sz.w + ':' + sz.h + ':(ow-iw)/2:(oh-ih)/2,fps=25,format=yuv420p';
        try { F.onProgress && F.onProgress(function (r) { o.onProgress && o.onProgress(Math.min(0.9, r * 0.9), 'ffmpeg'); }); } catch (_) {}
        var pre = ['-ss', String(o.start), '-t', String(total)];
        var kbps = Math.round((o.ceilingBps || 0) / 1000);
        var rc = kbps > 0 ? ['-maxrate', kbps + 'k', '-bufsize', (2 * kbps) + 'k'] : [];
        var r = await F.processStreaming(o.file, ['-an', '-vf', vf, '-c:v', 'libx264', '-preset', 'ultrafast', '-crf', '30']
            .concat(rc, ['-pix_fmt', 'yuv420p']), 'vsnd-proxy.mp4', null, pre);
        var blob = r && (r.blob || r);
        if (!blob || !(blob.size > 1000)) throw new Error('ffmpeg-empty');
        var got = await analyseByPlayback(blob, total, o);
        return { proxy: blob, lane: 'ffmpeg', duration: total, width: sz.w, height: sz.h,
                 cuts: got.cuts, frames: got.frames, thumbs: got.thumbs };
    }

    /* The FFmpeg lane cannot hand us every frame, so the analysis seeks through
       the finished proxy instead — 4 samples a second is enough to find cuts
       (a cut is located to ±0.25 s, which a sound designer would call exact). */
    async function analyseByPlayback(blob, total, o) {
        var v = document.createElement('video');
        v.muted = true; v.playsInline = true; v.preload = 'auto';
        var url = URL.createObjectURL(blob);
        v.src = url;
        // Visible-time deadline: Chromium does not load media in a hidden tab, so
        // wall-clock would turn «the person looked away» into «the file is broken».
        var later = w.__visibleTimeout || function (fn, ms) { var id = setTimeout(fn, ms); return function () { clearTimeout(id); }; };
        await new Promise(function (res, rej) {
            var stop = later(function () { rej(new Error('proxy-load-timeout')); }, 20000);
            v.onloadeddata = function () { if (stop) stop(); res(); };
            v.onerror = function () { if (stop) stop(); rej(new Error('proxy-unplayable')); };
        });
        var W = v.videoWidth, H = v.videoHeight, hist = Hist(), scores = [], prev = null;
        var col = Collector(total);
        var cv = new OffscreenCanvas(W, H), cx = cv.getContext('2d');
        var step = total > 90 ? 0.5 : 0.25;
        for (var t = 0; t < total; t += step) {
            if (o.signal && o.signal.aborted) throw new DOMException('aborted', 'AbortError');
            await new Promise(function (res) {
                var done = false, stop = null;
                var fin = function () { if (!done) { done = true; if (stop) stop(); res(); } };
                v.onseeked = fin; stop = later(fin, 1500);
                v.currentTime = Math.min(total - 0.04, t);
            });
            cx.drawImage(v, 0, 0, W, H);
            var h = hist.of(cv, W, H);
            scores.push({ t: t, s: prev ? dist(h, prev) : 0 });
            prev = h;
            col.maybe(cv, W, H, t);
            o.onProgress && o.onProgress(0.9 + 0.09 * (t / total), 'analyse');
        }
        URL.revokeObjectURL(url);
        var got = await col.done();
        return { cuts: cutsFrom(scores, total), frames: got.frames, thumbs: got.thumbs };
    }

    /* o.maxBytes — the upload door (the server's cap): the proxy is encoded
       under proxyCeiling() and, if an encoder still overshot past the door,
       encoded once more under a ceiling scaled by how far it overshot. */
    async function run(o) {
        var t0 = performance.now();
        var errs = [];
        o.ceilingBps = proxyCeiling(+o.maxBytes || 0, (o.end - o.start) || 0);
        if (o.preferLane !== 'ffmpeg') {
            try {
                var a = await viaWebCodecs(o);
                if (o.maxBytes > 0 && a.proxy.size > o.maxBytes && o.ceilingBps > 0) {
                    ae(o.slug, 'proxy_over_door', { kb: Math.round(a.proxy.size / 1024), cap_kb: Math.round(o.maxBytes / 1024), bps: o.ceilingBps });
                    o.ceilingBps = Math.floor(o.ceilingBps * PROXY_FILL * o.maxBytes / a.proxy.size);
                    a = await viaWebCodecs(o);
                }
                ae(o.slug, 'prepare_done', { lane: 'webcodecs', ms: Math.round(performance.now() - t0), dur: Math.round(a.duration), cuts: a.cuts.length, kb: Math.round(a.proxy.size / 1024), ceil: Math.round((o.ceilingBps || 0) / 1000) });
                return a;
            } catch (e) {
                if (e && e.name === 'AbortError') throw e;
                errs.push('wc:' + String((e && e.message) || e).slice(0, 80));
            }
        }
        try {
            var b = await viaFfmpeg(o);
            ae(o.slug, 'prepare_done', { lane: 'ffmpeg', ms: Math.round(performance.now() - t0), dur: Math.round(b.duration), cuts: b.cuts.length, kb: Math.round(b.proxy.size / 1024), why: errs.join('|') });
            return b;
        } catch (e2) {
            if (e2 && e2.name === 'AbortError') throw e2;
            errs.push('ff:' + String((e2 && e2.message) || e2).slice(0, 80));
            ae(o.slug, 'prepare_failed', { why: errs.join('|') });
            var err = new Error('prepare-failed');
            err.detail = errs.join('|');
            throw err;
        }
    }

    w.VideoSoundPrepare = { run: run, cutsFrom: cutsFrom, outSize: outSize, proxyCeiling: proxyCeiling };
})(window);
